import { useCallback, useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'

import styles from './FilesCategoryList.module.css'
import { Delete, Download } from '@factorialco/f0-react/icons/app'
import {
  DeletePayrollCycleFileDocument,
  DeletePayrollCycleFileMutation,
  DeletePayrollResultDocument,
  DeletePayrollResultMutation,
  DownloadPayrollCycleFileDocument,
  DownloadPayrollCycleFileMutation,
  PayrollRunFileCategoryFragment,
  PayrollRunFileFragment,
  PayrollRunFilesDocument
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { download } from 'modules/core/lib/download'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import DeleteFileModal from 'modules/bookkeepers/components/PayrollCycle/DeleteFileModal'

interface FilesDataCollectionProps {
  companyId: string
  payrollRunId: string
  categories: Array<{ id: string } & PayrollRunFileCategoryFragment>
  emptyState: React.ComponentProps<typeof OneDataCollection>['emptyStates']
  refetchTrigger?: number
  bookkeeperContext?: { isBookkeeperView: boolean }
  sidebarView?: boolean
  onImport?: (fileId: string) => void
  importedFileIds?: Set<string>
  hasCompensationsProfessionalsConnection?: boolean
}

type ImportFileStatus = 'to_process' | 'pending' | 'finished' | 'completed_with_errors' | 'error'

const STATUS_VARIANT_MAPPING: Record<
  ImportFileStatus,
  'neutral' | 'info' | 'positive' | 'warning' | 'critical'
> = {
  to_process: 'neutral',
  pending: 'info',
  finished: 'positive',
  completed_with_errors: 'warning',
  error: 'critical'
}

function isImportProcessStatus(value: string): value is ImportFileStatus {
  return (
    value === 'pending' ||
    value === 'finished' ||
    value === 'completed_with_errors' ||
    value === 'error'
  )
}

function getFileImportStatus(file: PayrollRunFileFragment): ImportFileStatus | undefined {
  if (!file.payrollResultId) return 'to_process'
  const rawStatus = file.payrollResult?.importProcess?.status
  if (rawStatus && isImportProcessStatus(rawStatus)) {
    return rawStatus
  }
  return undefined
}

function getStatusTranslation(status: ImportFileStatus): string {
  switch (status) {
    case 'to_process':
      return i18n.t(
        'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.import_process_statuses.to_process'
      )
    case 'pending':
      return i18n.t(
        'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.import_process_statuses.pending'
      )
    case 'finished':
      return i18n.t(
        'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.import_process_statuses.finished'
      )
    case 'completed_with_errors':
      return i18n.t(
        'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.import_process_statuses.completed_with_errors'
      )
    case 'error':
      return i18n.t(
        'compensations.PayrollRunCompensationsList.manage_payroll_results.modal.import_process_statuses.error'
      )
  }
}

const FilesCategoryList = ({
  companyId,
  payrollRunId,
  categories,
  emptyState,
  refetchTrigger,
  bookkeeperContext,
  sidebarView = false,
  onImport,
  importedFileIds,
  hasCompensationsProfessionalsConnection
}: FilesDataCollectionProps) => {
  const [files, setFiles] = useState<PayrollRunFileFragment[]>([])
  const [fileToDelete, setFileToDelete] = useState<PayrollRunFileFragment | null>(null)

  const getCategoryCount = useCallback(
    (fileCategoryId: string) => {
      return files.filter((file) => file.fileCategoryId === fileCategoryId).length
    },
    [files]
  )

  const [deleteFile, { loading: deleting }] = useMutation(DeletePayrollCycleFileDocument, {
    extractorFn: (data: DeletePayrollCycleFileMutation) =>
      data.compensationsMutations.deleteCompensationsFile
  })

  const [deletePayrollResult] = useMutation(DeletePayrollResultDocument, {
    extractorFn: (data: DeletePayrollResultMutation) => data.payrollMutations.deletePayrollResult
  })

  const [downloadFile] = useMutation(DownloadPayrollCycleFileDocument, {
    extractorFn: (data: DownloadPayrollCycleFileMutation) =>
      data.compensationsMutations.downloadCompensationsFile
  })

  const handleDownload = useCallback(
    async (file: PayrollRunFileFragment) => {
      const response = await downloadFile({
        variables: { id: file.id },
        context: bookkeeperContext
      })

      const result = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('bookkeepers.clients.payroll_cycles.documents.errors.download')
        })
      })

      if (result.success && result.data.file?.downloadUrl) {
        download(result.data.file.downloadUrl, file.fileName)
      }
    },
    [downloadFile, bookkeeperContext]
  )

  const handleDelete = useCallback(
    async (file: PayrollRunFileFragment) => {
      track(trackingEvents.bookkeeper.cycles.documentDeletedInProfessionals, {
        company_id: companyId,
        file_id: file.id
      })

      const isPayrollResultFile =
        categories.find((c) => c.id === file.fileCategoryId)?.categoryType === 'payroll_result'

      // When FF is on and the file is a payroll result: delete both the file AND the payroll result
      if (hasCompensationsProfessionalsConnection && isPayrollResultFile && file.payrollResultId) {
        const [fileResponse, payrollResultResponse] = await Promise.all([
          deleteFile({
            variables: { fileId: file.id },
            context: bookkeeperContext,
            refetchQueries: [
              {
                query: PayrollRunFilesDocument,
                variables: {
                  companyId,
                  payrollRunId,
                  fileCategoryIds: categories.map((category) => category.id)
                },
                context: bookkeeperContext
              }
            ]
          }),
          deletePayrollResult({
            variables: { id: file.payrollResultId }
          })
        ])

        const fileResult = handleResult(fileResponse, () => {})
        const payrollResult = handleResult(payrollResultResponse, () => {})

        if (fileResult.success && payrollResult.success) {
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.deleted_successfully')
          })
          setFileToDelete(null)
        } else {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('bookkeepers.clients.payroll_cycles.documents.errors.delete')
          })
        }
        return
      }

      // Default: delete only the file (bookkeeper-uploaded or FF off)
      const response = await deleteFile({
        variables: { fileId: file.id },
        context: bookkeeperContext,
        refetchQueries: [
          {
            query: PayrollRunFilesDocument,
            variables: {
              companyId,
              payrollRunId,
              fileCategoryIds: categories.map((category) => category.id)
            },
            context: bookkeeperContext
          }
        ]
      })

      const result = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('bookkeepers.clients.payroll_cycles.documents.errors.delete')
        })
      })

      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.deleted_successfully')
        })

        setFileToDelete(null)
      }
    },
    [
      deleteFile,
      deletePayrollResult,
      companyId,
      payrollRunId,
      categories,
      bookkeeperContext,
      hasCompensationsProfessionalsConnection
    ]
  )

  const sourceFiles = useGraphqlDataCollectionSource(
    {
      document: PayrollRunFilesDocument,
      queryContext: bookkeeperContext,
      mapData: (data) => {
        const nodes = data.compensations.filesConnection.nodes
          .map((item) => {
            return {
              item,
              createdAt: stringToDate(item.createdAt, 'backend_datetime')
            }
          })
          .sort((a, b) => {
            return b.createdAt.diff(a.createdAt)
          })
          .map(({ item }) => ({
            ...item
          }))
        setFiles(nodes)
        return nodes
      },
      mapPagination: (total) => ({
        total: total.compensations.filesConnection.totalCount ?? 0
      }),
      paginationType: 'infinite-scroll',
      mapVariables: () => {
        return {
          companyId,
          payrollRunId,
          fileCategoryIds: categories.map((category) => category.id)
        }
      },
      filters: {},
      presets: [],
      itemActions: (file: PayrollRunFileFragment) => [
        ...(onImport &&
        file.payrollResultId === null &&
        !importedFileIds?.has(file.id) &&
        categories.find((c) => c.id === file.fileCategoryId)?.categoryType === 'payroll_result'
          ? [
              {
                label: i18n.t('common.process'),
                icon: Download,
                onClick: () => onImport(file.id),
                type: 'primary' as const
              }
            ]
          : []),
        {
          label: i18n.t('common.download'),
          icon: Download,
          onClick: () => handleDownload(file)
        },
        ...(file._policies?.delete?.can
          ? [
              {
                label: i18n.t('common.delete'),
                icon: Delete,
                onClick: () => setFileToDelete(file),
                critical: true
              }
            ]
          : [])
      ],
      grouping: {
        collapsible: true,
        mandatory: true,
        hideSelector: true,
        defaultOpenGroups: true,
        groupBy: {
          fileCategoryId: {
            name: 'Category',
            label(fileCategoryId) {
              const categoryKey = categories.find(
                (category) => category.id === fileCategoryId
              )?.categoryType

              switch (categoryKey) {
                case 'payroll_result':
                  return i18n.t(
                    'bookkeepers.clients.payroll_cycles.documents.file_categories.payroll_result'
                  )
                case 'payslips':
                  return i18n.t(
                    'bookkeepers.clients.payroll_cycles.documents.file_categories.payslips'
                  )
                case 'payment_method':
                  return i18n.t(
                    'bookkeepers.clients.payroll_cycles.documents.file_categories.payment_method'
                  )
                case 'others':
                  return i18n.t(
                    'bookkeepers.clients.payroll_cycles.documents.file_categories.others'
                  )
                case 'exported':
                  return i18n.t(
                    'bookkeepers.clients.payroll_cycles.documents.file_categories.exported'
                  )
                default:
                  return ''
              }
            },
            itemCount: (fileCategoryId) => getCategoryCount(String(fileCategoryId))
          }
        }
      }
    },
    [refetchTrigger]
  )

  return (
    <>
      <div className={hasCompensationsProfessionalsConnection ? styles.listWithStatus : undefined}>
        <OneDataCollection
          storage={false}
          source={sourceFiles}
          emptyStates={emptyState}
          visualizations={[
            {
              type: 'list',
              options: {
                itemDefinition: (item: PayrollRunFileFragment) => {
                  const creationDate = i18nDateToString(
                    stringToDate(item.createdAt, 'backend_datetime'),
                    'ui_short_with_time'
                  )
                  return {
                    title: item.fileName,
                    description: sidebarView
                      ? [`${item.createdBy?.firstName} ${item.createdBy?.lastName}`, creationDate]
                      : undefined,
                    avatar: {
                      type: 'file',
                      file: {
                        name: item.fileName,
                        type: ''
                      }
                    }
                  }
                },
                fields: sidebarView
                  ? [
                      ...(hasCompensationsProfessionalsConnection
                        ? [
                            {
                              label: '',
                              render: (item: PayrollRunFileFragment) => {
                                const isPayrollResult =
                                  categories.find((c) => c.id === item.fileCategoryId)
                                    ?.categoryType === 'payroll_result'
                                if (!isPayrollResult) return { type: 'text' as const, value: '' }

                                const status = getFileImportStatus(item)
                                if (status) {
                                  return {
                                    type: 'status' as const,
                                    value: {
                                      label: getStatusTranslation(status),
                                      status: STATUS_VARIANT_MAPPING[status]
                                    }
                                  }
                                }

                                // Files with a payroll result linked but no import process
                                // status yet (upload just happened / process not started)
                                if (item.payrollResultId) {
                                  return {
                                    type: 'status' as const,
                                    value: {
                                      label: i18n.t(
                                        'compensations.PayrollRunCompensationsList.activity_sidepanel.documents.documents.in_progress_status'
                                      ),
                                      status: 'info' as const
                                    }
                                  }
                                }

                                return { type: 'text' as const, value: '' }
                              }
                            }
                          ]
                        : [])
                    ]
                  : [
                      {
                        label: i18n.t(
                          'bookkeepers.clients.payroll_cycles.documents.list.created_at'
                        ),
                        render: (item: PayrollRunFileFragment) => ({
                          type: 'text',
                          value: i18nDateToString(
                            stringToDate(item.createdAt, 'backend_datetime'),
                            'ui_short_with_time'
                          )
                        })
                      },
                      {
                        label: i18n.t(
                          'bookkeepers.clients.payroll_cycles.documents.list.created_by'
                        ),
                        render: (item: PayrollRunFileFragment) => ({
                          type: 'person',
                          value: {
                            firstName: item.createdBy?.firstName ?? '',
                            lastName: item.createdBy?.lastName ?? '',
                            src: buildUrl(item.createdBy?.avatar ?? '')
                          }
                        })
                      }
                    ]
              }
            }
          ]}
        />
      </div>
      {fileToDelete && (
        <DeleteFileModal
          onClose={() => setFileToDelete(null)}
          onSubmit={() => handleDelete(fileToDelete)}
          deleting={deleting}
        />
      )}
    </>
  )
}

export default FilesCategoryList
